/**
 * Everything the screen says, and the arithmetic behind it.
 *
 * It lives apart from the screen for two reasons. It is the only part of the
 * interface that can be tested without a phone, and it is the part where
 * getting it wrong matters: a message saying "listo" when the reading is worth
 * sixty metres is how a wrong coordinate ends up in a map.
 *
 * The wording follows the same rule as the rest of the project: it says what
 * to do, not what failed. And when a point is poor it says **which** of the
 * three things went wrong, because "poco fiable" tells you nothing about
 * whether standing still for ten seconds would fix it.
 */
import type { FixQuality } from "./fix.js";
import type { GeoProblem } from "./geo.js";
import type { Spot } from "./position.js";

/** What to do about it, never what broke. */
export function problemText(problem: GeoProblem): string {
  switch (problem) {
    case "denied":
      return "Falta el permiso de ubicación. Actívalo para esta página en los ajustes del navegador.";
    case "unavailable":
      return "El móvil no consigue situarse. Sal a cielo abierto y espera unos segundos.";
    case "timeout":
      return "Está tardando. Sigue andando: en cuanto vea satélites se coloca solo.";
    case "unsupported":
      return "Este navegador no sabe decir dónde estás. Prueba con Chrome o Safari.";
  }
}

/** "hace 3 s", "hace 2 min": how stale the newest reading already is. */
export function ageText(ageS: number): string {
  const seconds = Math.round(Math.max(0, ageS));
  if (seconds < 3) return "ahora mismo";
  if (seconds < 90) return `hace ${seconds} s`;
  return `hace ${Math.round(seconds / 60)} min`;
}

/** "8 lecturas", and the singular right. */
export function samplesText(samples: number): string {
  return samples === 1 ? "1 lectura" : `${samples} lecturas`;
}

/**
 * The line at the top: the margin, how many readings and how fresh they are.
 *
 * The count is there and not hidden because it is the thing the walker can
 * actually change. Standing still for ten more seconds turns two readings into
 * twelve, and that is the difference between a point that can be published and
 * one that cannot.
 */
export function statusText(spot: Spot | null, ageS: number): string {
  if (spot === null) return "Buscando dónde estás…";
  const stale = ageS >= 5 ? ` · ${ageText(ageS)}` : "";
  return `${spot.accuracyM} m · ${samplesText(spot.samples)}${stale}`;
}

/** The three numbers, spelled out, for the point's own card. */
export function spotDetail(spot: Spot): string {
  return `${spot.accuracyM} m de margen · ${samplesText(spot.samples)} · ±${spot.spreadM} m entre ellas`;
}

/** What the sheet says while the point is still being improved. */
export function refiningText(spot: Spot): string {
  return `Afinando el punto: ${samplesText(spot.samples)}. Quédate quieto un momento.`;
}

/**
 * Why this point is not good enough, in the walker's terms.
 *
 * Naming the actual cause is the whole value: "pocas lecturas" is fixed by
 * waiting, "las lecturas no se ponen de acuerdo" is fixed by moving away from
 * the wall, and "el móvil da mucho error" often is not fixable at all. One
 * generic warning would leave all three looking the same.
 */
export function spotWarning(spot: Spot, quality: FixQuality): string | null {
  if (quality === "good") return null;
  if (spot.samples < 3) {
    return `Solo ${samplesText(spot.samples)}: no hay con qué comparar, así que el punto puede estar donde sea. Quédate quieto unos segundos y vuelve a apuntarlo.`;
  }
  if (spot.spreadM > 20) {
    return `Las lecturas quedaron a ${spot.spreadM} m unas de otras: el móvil no se aclaraba, seguramente por árboles o casas. No sirve para decir «está justo aquí».`;
  }
  if (spot.accuracyM > 25) {
    return `El móvil daba ${spot.accuracyM} m de error. Sirve para saber por qué tramo ibas, no para situar nada.`;
  }
  return "Vale para el tramo, no para el punto exacto. Si puedes, espera unos segundos y apúntalo otra vez.";
}

/** "13:15" out of the stored timestamp. */
export function clockOf(iso: string): string {
  const match = /T(\d{2}):(\d{2})/.exec(iso);
  return match === null ? "" : `${match[1] ?? ""}:${match[2] ?? ""}`;
}

/** How many points are still to be exported, said in words. */
export function exportLabel(pending: number, online: boolean): string {
  if (pending === 0) return "Nada nuevo que exportar";
  const what = pending === 1 ? "Exportar 1 punto" : `Exportar ${pending} puntos`;
  // Only mentioned when there is enough at stake to be worth interrupting for.
  // Below that it would be noise on every single screen.
  return online && pending >= 10 ? `${what} · hay cobertura` : what;
}

/** Compact size for a button or a short warning. */
export function bytesText(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
}

/** The export button's at-a-glance preview. */
export function exportPreviewLabel(
  points: number,
  photos: number,
  bytes: number,
  online = false,
): string {
  const photoWord = photos === 1 ? "foto" : "fotos";
  return `${exportLabel(points, online)} · ${photos} ${photoWord} · ${bytesText(bytes)}`;
}

/** The 📋 button: bare when there is nothing left, the count once there is. */
export function pendientesLabel(count: number): string {
  return count === 0 ? "📋" : `📋 ${count}`;
}

/** The counter in the top bar. */
export function countText(total: number, pending: number): string {
  if (total === 0) return "";
  return pending === total ? `${total}` : `${pending}/${total}`;
}

const MONTHS = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

/** The day part of a point's id: `20260824-131502-847` → `20260824`. */
export function dayOf(id: string): string {
  return id.slice(0, 8);
}

/**
 * A day written out in full: "24 de agosto de 2026".
 *
 * The year is there on purpose. This is read on a screen where somebody is
 * about to delete a day's walking for good, and "24 de agosto" alone would be
 * ambiguous the moment there is a second season.
 */
export function dayLabel(day: string): string {
  const year = day.slice(0, 4);
  const month = MONTHS[Number(day.slice(4, 6)) - 1];
  const date = Number(day.slice(6, 8));
  if (month === undefined || !Number.isFinite(date) || date === 0) return day;
  return `${date} de ${month} de ${year}`;
}

/** "12 puntos · 8 exportados", or just the count when none have gone out. */
export function daySummary(total: number, exported: number): string {
  const points = total === 1 ? "1 punto" : `${total} puntos`;
  if (exported === 0) return `${points} · sin exportar`;
  if (exported === total) return `${points} · todos exportados`;
  return `${points} · ${exported} exportados`;
}

/**
 * The warning before deleting, which changes with what is at stake.
 *
 * Deleting something already sitting in a file on a laptop is tidying up.
 * Deleting something that only exists in this phone is throwing away a walk,
 * and the two must not read the same.
 */
export function deleteWarning(total: number, exported: number): string {
  const missing = total - exported;
  if (missing === 0) return "Ya están todos en un fichero exportado. No se pierde nada.";
  const what = missing === 1 ? "1 punto que no ha salido" : `${missing} puntos que no han salido`;
  return `Cuidado: hay ${what} del móvil. Si los borras, no hay forma de recuperarlos.`;
}

/**
 * How a point reads in the list: what it is, plus whatever was answered.
 *
 * A field answered "otro" shows its own detail instead of the bare word
 * "otro" — "Sellan aquí · en la ermita de al lado" reads as an answer;
 * "Sellan aquí · otro · en la ermita de al lado" reads as a glitch. The
 * `<id>_otro` entry itself is never listed a second time on its own.
 */
export function pointSummary(label: string, fields: Record<string, string | number>): string {
  const answers = Object.entries(fields)
    .filter(([key]) => !key.endsWith("_otro"))
    .map(([key, value]) => {
      const detail = fields[`${key}_otro`];
      return value === "otro" && typeof detail === "string" && detail !== "" ? detail : value;
    })
    .map((value) => String(value))
    .filter((value) => value !== "");
  return answers.length === 0 ? label : `${label} · ${answers.join(" · ")}`;
}
